import styled from "@emotion/styled";
import { FC, useState } from "react";
import { Field, useForm } from "react-final-form";
import { useSelector } from "react-redux";

import { DesignToken, ShadowToken, SpacingToken, Text } from "@livechat/design-system-react-components";

import { AutoAssignmentInfoTooltip } from "components/autoassignment/AutoAssignmentInfoTooltip";
import Flex from "components/layout/Flex";
import { ModalPortal } from "components/modal";
import { agentCanBeAssignedToTickets } from "helpers/agent";
import { IAgent, selectAgentsAlphabetically } from "store/entities/agents";
import { ITeam } from "store/entities/teams";
import { withVar } from "themes/helpers";
import { areSimilar } from "utils/array";

import { ManageAgentsButton } from "./ManageAgentsButton";
import { MemberItem } from "./MemberItem";
import { MembersModal } from "./MembersModal";

const ValidationError = styled.div`
  font-size: 14px;
  color: ${withVar(DesignToken.ContentBasicNegative)};
`;

interface IProps {
  agents: IAgent[];
  team: ITeam | null;
  value: string[];
  validationMessage?: string;
  onChange(agentIds: string[]): void;
}

function getTeamAgents(agentIds: string[], agents: IAgent[]): IAgent[] {
  return agents.filter(agent => agentIds.includes(agent.id));
}

export const Members: FC<IProps> = ({ agents, team, value, validationMessage, onChange }) => {
  const allAgents = useSelector(selectAgentsAlphabetically);
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const form = useForm();
  const teamAgents = getTeamAgents(value, agents);

  const onConfirmMembersModal = (agentIds: string[]): void => {
    const currentIds = value;
    const newIds = agentIds;

    const removedIds = currentIds.filter(id => !newIds.includes(id));
    const addedIds = newIds.filter(id => {
      const agent = allAgents.find(agent => agent.id === id);
      return agent && agentCanBeAssignedToTickets(agent) && !currentIds.includes(id);
    });

    const currentAutoassignedAgentIDs = form.getFieldState("autoassignmentAgentIDs")?.value ?? [];
    const newAutoassignedAgentIDs = currentAutoassignedAgentIDs
      .concat(addedIds)
      .filter((id: string) => !removedIds.includes(id));

    onChange(agentIds);
    form.change("autoassignmentAgentIDs", newAutoassignedAgentIDs);
    toggleMembersModal();
  };

  const toggleMembersModal = (): void => {
    setIsMembersModalOpen(prevState => !prevState);
  };

  return (
    <Flex column gap={withVar(SpacingToken.Spacing4)}>
      <ManageAgentsButton onClick={toggleMembersModal} />
      {validationMessage && <ValidationError>{validationMessage}</ValidationError>}
      <Flex column>
        {teamAgents.length > 0 && (
          <Flex boxShadow={withVar(ShadowToken.DividerBottom)}>
            <HeaderText>Name</HeaderText>
            <HeaderText>
              Auto-assignment
              <AutoAssignmentInfoTooltip location="team" />
            </HeaderText>
          </Flex>
        )}
        <Field<string[]> name="autoassignmentAgentIDs" isEqual={areSimilar}>
          {({ input }) =>
            getTeamAgents(value, agents).map(agent => <MemberItem key={agent.id} agent={agent} input={input} />)
          }
        </Field>
      </Flex>
      {isMembersModalOpen && (
        <ModalPortal>
          <MembersModal
            agents={agents}
            team={team}
            value={value}
            onConfirm={onConfirmMembersModal}
            onCancel={toggleMembersModal}
          />
        </ModalPortal>
      )}
    </Flex>
  );
};

const HeaderText = styled(Text)`
  font-weight: 600;
  flex-basis: 50%;
  padding-block: ${withVar(SpacingToken.Spacing2)};
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing1)};
`;
